import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
} from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import { useVisit } from "../../../contexts/VisitContext";
import { useAuth } from "../../../contexts/AuthContext";
import { parseDateToTimestamp, todayLocalDateString } from "../../../utils/date";
import { Visit, VisitPaymentStatus } from "@visitproof/types";
import {
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

interface MarkMonitorPaidButtonProps {
  visit: Visit;
}

const MarkMonitorPaidButton: React.FC<MarkMonitorPaidButtonProps> = ({ visit }) => {
  const { updateVisit } = useVisit();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState<string>(
    todayLocalDateString()
  );
  const [paymentNotes, setPaymentNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPaid = visit.monitorPaymentStatus === "paid";

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      await updateVisit(visit.id, {
        monitorPaymentStatus: "paid" as VisitPaymentStatus,
        monitorPaymentDate: parseDateToTimestamp(paymentDate),
        monitorPaymentNotes: paymentNotes || undefined,
        monitorPaymentMarkedBy: user?.uid,
      });

      clearForm();
    } catch (error) {
      console.error("Error marking monitor as paid:", error);
      alert("Failed to mark monitor as paid. Please try again.");
    }

    setIsSubmitting(false);
  };

  const handleMarkAsUnpaid = async () => {
    setIsSubmitting(true);

    try {
      await updateVisit(visit.id, {
        monitorPaymentStatus: "unpaid" as VisitPaymentStatus,
        monitorPaymentDate: undefined,
        monitorPaymentNotes: undefined,
        monitorPaymentMarkedBy: undefined,
      });
    } catch (error) {
      console.error("Error marking monitor as unpaid:", error);
      alert("Failed to mark monitor as unpaid. Please try again.");
    }

    setIsSubmitting(false);
  };

  const clearForm = () => {
    setPaymentDate(todayLocalDateString());
    setPaymentNotes("");
    setIsOpen(false);
  };

  if (isPaid) {
    return (
      <Button
        outline
        onClick={handleMarkAsUnpaid}
        disabled={isSubmitting}
        className="flex items-center gap-2"
      >
        <XCircleIcon className="h-4 w-4" />
        {isSubmitting ? "Updating..." : "Mark as Unpaid"}
      </Button>
    );
  }

  return (
    <>
      <Button
        color="green"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
      >
        <UserIcon className="h-4 w-4" />
        Mark Monitor Paid
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Mark Monitor as Paid</DialogTitle>
        <DialogDescription>
          Record the payment date for the monitor assigned to this visit.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </Field>

              <Field>
                <Label>Payment Notes (optional)</Label>
                <Textarea
                  value={paymentNotes}
                  placeholder="e.g., Direct deposit, Check #1234, etc."
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={3}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button
            color="green"
            disabled={!paymentDate || isSubmitting}
            onClick={handleSubmit}
            className="flex items-center gap-2"
          >
            <CheckCircleIcon className="h-4 w-4" />
            {isSubmitting ? "Saving..." : "Confirm Payment"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarkMonitorPaidButton;
